/**
 * VÉRAULT Content Studio — Website Editor (Page List)
 *
 * Internal luxury publishing dashboard, not a developer tool.
 *
 * Pages managed in v1: Home, About.
 * Future passes add Services, Plan, Destinations, Journal landing, etc.
 */
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Globe,
  CheckCircle2,
  Clock,
  Eye,
  Pencil,
} from "lucide-react";
import {
  Surface,
  Heading,
  EditorialRule,
  PortalButton,
  Skeleton,
  Empty,
  AIWhisper,
  api,
  fmt,
  motion as portalMotion,
} from "../../../index";

const { fadeInUp, stagger } = portalMotion;

function PageCard({ p }) {
  const published = p.is_published;
  const draftAhead = p.draft_blocks_count !== p.block_count_published || (p.draft_updated_at && (!p.published_at || new Date(p.draft_updated_at) > new Date(p.published_at)));
  // Backend returns block_count_draft / block_count_published — guard for both.
  const draftCount = p.block_count_draft ?? 0;
  const pubCount = p.block_count_published ?? 0;
  const ahead = draftCount !== pubCount || (p.draft_updated_at && (!p.published_at || new Date(p.draft_updated_at) > new Date(p.published_at)));
  return (
    <Surface variant="default" padded={false} className="overflow-hidden" data-testid={`website-page-card-${p.slug}`}>
      <div className="px-6 pt-6 pb-4 border-b border-[rgba(0,0,0,0.04)]">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
              {p.public_path || `/${p.slug}`}
            </p>
            <h3 className="mt-1.5 font-serif text-2xl text-[hsl(var(--foreground))]">{p.title}</h3>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Wave 34 — "Live on site" badge removed. The public site
                always renders the React pages; the studio publishes
                drafts for /preview/:slug review only. */}
            {published ? (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-[0.18em] bg-[hsl(var(--accent-sage,170_18%_42%))]/15 text-[hsl(var(--accent-sage,170_18%_42%))]">
                <CheckCircle2 className="size-3" strokeWidth={2} />
                Published
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-[0.18em] bg-[hsl(var(--background-paper))] text-[hsl(var(--foreground-muted))]">
                <Clock className="size-3" strokeWidth={1.5} />
                Draft only
              </span>
            )}
            {ahead && published && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-[0.18em] bg-[hsl(var(--accent-gold))]/15 text-[hsl(var(--accent-gold))]">
                Draft ahead
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="px-6 py-4 grid sm:grid-cols-3 gap-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">Draft blocks</p>
          <p className="font-serif text-2xl tabular-nums">{draftCount}</p>
        </div>
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">Published blocks</p>
          <p className="font-serif text-2xl tabular-nums">{pubCount}</p>
        </div>
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">Last edited</p>
          <p className="font-body text-[13.5px]">{p.draft_updated_at ? fmt.formatDate(p.draft_updated_at) : "—"}</p>
        </div>
      </div>

      <div className="border-t border-[rgba(0,0,0,0.05)] px-6 py-3 flex items-center justify-end gap-2">
        {published && (
          <PortalButton
            as="a"
            href={`/preview/${p.slug}?mode=published`}
            target="_blank"
            rel="noreferrer"
            variant="ghost"
            size="sm"
            icon={<Eye className="size-3.5" />}
            data-testid={`website-preview-published-${p.slug}`}
          >
            View live preview
          </PortalButton>
        )}
        <PortalButton
          as={Link}
          to={`/portal/advisor/content/website/${p.slug}`}
          variant="primary"
          size="sm"
          icon={<Pencil className="size-3.5" />}
          iconAfter={<ArrowRight className="size-3.5" strokeWidth={1.5} />}
          data-testid={`website-edit-${p.slug}`}
        >
          Open editor
        </PortalButton>
      </div>
    </Surface>
  );
}

export default function WebsiteEditorList() {
  const [pages, setPages] = React.useState(null);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    api
      .get("/site-cms/pages")
      .then(({ data }) => alive && setPages(Array.isArray(data) ? data : []))
      .catch(() => alive && setError("Couldn't load the website pages."));
    return () => { alive = false; };
  }, []);

  return (
    <motion.div initial="initial" animate="animate" variants={stagger} className="space-y-10" data-testid="website-editor-list">
      <motion.div variants={fadeInUp}>
        <AIWhisper label="The publishing room" testid="website-editor-whisper">
          Edit drafts of the live site&apos;s six pages — Home, Philosophy, Services,
          Destinations, Journal, Begin Your Experience — without leaving the studio.
          Branding (colours, typography, spacing) and the live layout are locked;
          the public site keeps its VÉRAULT bones. Drafts you publish here render
          on <em>/preview/:slug</em> for review.
        </AIWhisper>
      </motion.div>

      <motion.div variants={fadeInUp}>
        <EditorialRule label={pages ? `${pages.length} page${pages.length === 1 ? "" : "s"} managed` : "loading"} />
        <div className="mt-6">
          {error ? (
            <Empty title={error} icon={<Globe className="size-6" strokeWidth={1.25} />} />
          ) : !pages ? (
            <div className="grid sm:grid-cols-2 gap-5">
              {[0, 1].map((i) => <Skeleton key={i} className="h-56" />)}
            </div>
          ) : pages.length === 0 ? (
            <Empty title="No pages yet" icon={<Globe className="size-6" strokeWidth={1.25} />} />
          ) : (
            <ul className="grid sm:grid-cols-2 gap-5">
              {pages.map((p) => (
                <li key={p.slug}>
                  <PageCard p={p} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </motion.div>

      <motion.div variants={fadeInUp} className="pt-2">
        <p className="font-accent italic text-[13px] text-[hsl(var(--foreground-muted))] text-center">
          Six pages managed · the original React pages remain as the permanent fallback.
        </p>
      </motion.div>
    </motion.div>
  );
}
